import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MapMarker from 'components/icons/MapMarker';
import Mail from 'components/icons/Mail';
import CallIcon from 'components/icons/Call';
import Calendar from 'components/icons/Calendar';
import { Button } from 'components/components/atoms';
import breakpoints from 'utils/breakpoints';
import useBreakpoint from 'utils/hooks/useBreakpoint';

import cx from 'classnames';
import getMapLink from 'utils/getMapLink';
import { useSpring, animated } from '@react-spring/web';
import { useMeasure } from 'react-use';
import trackingEvents from 'utils/trackingEvents';
import { track } from 'utils/tracking';

import { colors } from 'utils/tailwindColors';
const CardContainer = styled.div`
  margin-top: 0.313rem;
  margin-bottom: 0.25rem;
  @media ${breakpoints.md} {
    margin-bottom: 0.25rem;
  }
`;
const Card = styled.div`
  margin-top: 1rem;
  margin-bottom: 2rem;
  @media ${breakpoints.md} {
    margin-bottom: 1rem;
  }
`;
const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  min-height: 1.625rem;
  width: fit-content;

  @media ${breakpoints.md} {
    ${({ islogo }) =>
      islogo
        ? ''
        : `${Card}:hover & {
      color: white;
      background-color: ${colors['mattamy-blue-light']};
    }`}
  }

  ${({ isMobile, islogo }) =>
    islogo ? '' : isMobile ? 'min-width: 7.5rem;' : 'min-width: 8rem;'}
`;
const CardTitle = styled.div`
  letter-spacing: -0.029rem;
`;
const CardDescription = styled.div.attrs({
  className:
    'font-graphie text-mattamy-blue text-base leading-relaxed md:leading-5  md:mb-0 flex items-start',
})`
  letter-spacing: 0.007rem;
`;
const ToggleScheduleContainer = styled.div`
  place-self: center;
`;
const Img = styled.img`
  min-height: 13.75rem;
  transition: transform 0.5s ease;

  @media ${breakpoints.md} {
    min-height: 18rem;
  }
`;
const ImageContainer = styled.div`
  @media ${breakpoints.md} {
    min-height: 18rem;
  }
`;
const ProductInfo = styled.div`
  @media ${breakpoints.lg} {
    min-height: 10rem;
  }
`;
const IconContainer = styled.div.attrs({
  className:
    'flex justify-center items-center w-30-mattamy h-30-mattamy bg-action-blue inline mr-2 rounded-full text-white',
})`
  min-width: 1.875rem;
`;
const StyledButton = styled(Button)`
  height: 3.125rem;
`;

const getNote = (ssn) => {
  if (ssn && ssn.date) {
    const date = ssn.date.split('-');
    const text = ssn.text;
    if (date && date.length > 2) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const dateNote = new Date(date[0], date[1], date[2]);
      dateNote.setHours(0, 0, 0, 0);
      // console.log({ now, dateNote, date });
      if (dateNote >= now) return text;
    }
  }
  return '';
};
const SaleCenterCommunityCard = (props) => {
  const {
    city,
    state,
    communityName,
    direction,
    emailAddress,
    phone,
    saleCenterHours,
    zipCode,
    cardImage,
    openNow,
    flagText,
    firstCTAText,
    firstCTALink,
    secondCTAText,
    secondCTALink,
    latitude,
    longitude,
    page,
    counter,
    numberPerPage,
    salesCenterNoteVM,
    gmbLink = {},
  } = props;
  const minCounter = (page - 1) * numberPerPage;
  const maxCounter = page * numberPerPage + 1;
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const isTablet = ['md'].includes(breakpoint);

  const imageSrc =
    (cardImage && cardImage.srcDesktop) || (cardImage && cardImage.src);

  const defaultHeight = '0px';
  const [contentHeight, setContentHeight] = useState(defaultHeight);
  const [scheduleOpen, toggleScheduleOpen] = useState(false);
  const [scheduleRefHeight, { height }] = useMeasure();
  const getProperHeight = () => {
    if (scheduleOpen) {
      return `${contentHeight}px`;
    }
    return '0px';
  };

  const expand = useSpring({
    height: getProperHeight(),
    pointerEvents: scheduleOpen ? 'all' : 'none',
  });
  const fade = useSpring({
    opacity: scheduleOpen ? 1 : 0,
    delay: scheduleOpen ? 200 : 0,
  });
  useEffect(() => {
    setContentHeight(height);
    window.addEventListener('resize', setContentHeight(height));

    return window.removeEventListener('resize', setContentHeight(height));
  }, [height]);
  const trackEventId = trackingEvents.BookAppointment;
  const saleCenterHoursFilter = saleCenterHours.filter(
    (e) => (e.hours && e.hours.length > 10) || e.displayText,
  );
  const renderSchedule = () => (
    <animated.div ref={scheduleRefHeight} style={fade}>
      <ol
        className='flex flex-col items-center justify-start w-full'
        aria-label='Show Schedule'
      >
        {saleCenterHoursFilter.map((h, index) => (
          <li
            key={index}
            className={cx(
              'flex flex-col justify-start w-full text-sm font-graphie text-left text-mattamy-blue',
              {
                'mb-4': index === saleCenterHoursFilter.length - 1,
              },
            )}
          >
            <div className='flex justify-start w-full'>
              <div className='w-24 font-bold text-right'>{h.day}</div>
              <div className='flex flex-col ml-2'>
                {h.hours && h.hours.length > 10 && (
                  <span className='text-left font-graphie whitespace-nowrap'>
                    {h.hours}
                  </span>
                )}
                {h.displayText && h.displayText.length > 3 && (
                  <span className='text-left font-graphie whitespace-nowrap'>
                    {h.displayText}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </animated.div>
  );
  const getClosedHour = () => {
    const weekdays = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];
    const d = new Date();
    let day = d.getDay();
    const hours = saleCenterHoursFilter.find((h) =>
      h.day.includes(weekdays[day - 1]),
    );
    const chour = hours && hours.hours && hours.hours.split('-');
    return chour && chour.length > 1 ? chour[1] : '';
  };
  return (
    <CardContainer
      className={minCounter < counter && maxCounter > counter ? '' : 'hidden'}
    >
      <Card className='w-full bg-mattamy-white flex flex-col lg:flex-row justify-center items-start'>
        <ImageContainer className='w-full h-full text-center relative rounded overflow-hidden md:w-4/12'>
          <div className='overflow-hidden rounded'>
            <Img
              src={imageSrc}
              alt={(cardImage && cardImage.alt) || ''}
              className={'bg-gray-300 object-cover w-full'}
            />
          </div>
        </ImageContainer>

        <div className='w-full h-full text-left md:w-8/12 flex flex-col'>
          {flagText && (
            <div className='flex flex-row lg:pl-5 mb-4'>
              <AvailableFlag
                isMobile={isMobile}
                islogo={false}
                className='flex items-center justify-center bg-white border-mattamy-blue-light border-2 border-mattamy-blue-light hover:md:bg-mattamy-blue-light
                mt-1 md:mt-0 hover:md:text-white text-mattamy-blue-light px-2'
              >
                <p className='px-1 text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1'>
                  {flagText}
                </p>
              </AvailableFlag>
            </div>
          )}

          <CardTitle className='font-trade-gothic-20 text-mattamy-blue text-3xl leading-8 md:mt-1 mb-6 block lg:pl-5'>
            <h3 aria-label={communityName}>{communityName}</h3>
            {getNote(salesCenterNoteVM) && (
              <div className='flex items-center mb-2'>
                <span className='font-graphie text-mattamy-blue text-base'>
                  {getNote(salesCenterNoteVM)}
                </span>
              </div>
            )}
          </CardTitle>

          <div className='flex flex-col md:flex-row'>
            <ProductInfo className='pt-2 pb-4 lg:pt-0 w-full md:w-1/2 flex flex-col justify-between'>
              <div className='block lg:pl-5'>
                {direction && (
                  <CardDescription className='items-start pb-4'>
                    <IconContainer aria-hidden='true'>
                      <MapMarker className='h-4 w-4' />
                    </IconContainer>
                    <div className='flex flex-col'>
                      <a
                        href={
                          gmbLink && gmbLink.href
                            ? gmbLink.href
                            : latitude && longitude
                            ? getMapLink(latitude + ',' + longitude)
                            : getMapLink(
                                direction +
                                  ' ' +
                                  city +
                                  ' ' +
                                  state +
                                  ' ' +
                                  zipCode,
                              )
                        }
                        className='text-mattamy-blue leading-7 font-graphie mb-2'
                      >
                        {'Get directions'}
                      </a>

                      <a
                        className='font-graphie leading-4 text-mattamy-blue mb-10'
                        href={
                          gmbLink && gmbLink.href
                            ? gmbLink.href
                            : latitude && longitude
                            ? getMapLink(latitude + ',' + longitude)
                            : getMapLink(
                                direction +
                                  ' ' +
                                  city +
                                  ' ' +
                                  state +
                                  ' ' +
                                  zipCode,
                              )
                        }
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label={
                          'Go to ' +
                          direction +
                          ' ' +
                          city +
                          ' ' +
                          state +
                          ' ' +
                          zipCode +
                          '. Opens in GoogleMaps'
                        }
                      >
                        {direction}
                        <br />
                        <span>
                          {!city || city.length === 0 ? '' : city + ', '}
                        </span>
                        <span>{state}</span>
                        <span>
                          {!zipCode || zipCode.length === 0
                            ? ''
                            : ', ' + zipCode}
                        </span>
                      </a>
                    </div>
                  </CardDescription>
                )}
                {emailAddress && (
                  <CardDescription className='items-start pb-4'>
                    <IconContainer aria-hidden='true'>
                      <Mail className='h-4 w-4' />
                    </IconContainer>
                    <a
                      className='break-all mt-1'
                      href={`mailto:${emailAddress}`}
                      aria-label={'Email to ' + emailAddress}
                    >
                      {emailAddress}
                    </a>
                  </CardDescription>
                )}
                {phone && (
                  <CardDescription>
                    <IconContainer aria-hidden='true'>
                      <CallIcon className='w-30-mattamy h-30-mattamy text-action-blue bg-white rounded-full' />
                    </IconContainer>
                    <a
                      className='break-all md:mt-1'
                      href={`tel:${phone}`}
                      aria-label={'Call to ' + phone}
                    >
                      {phone}
                    </a>
                  </CardDescription>
                )}
              </div>
            </ProductInfo>

            <ProductInfo className='pt-2 lg:pt-0 w-full md:w-1/2 flex flex-col justify-between'>
              {saleCenterHoursFilter.length > 0 && (
                <>
                  <CardDescription>
                    <IconContainer aria-hidden='true'>
                      <Calendar className='h-4 w-4' />
                    </IconContainer>
                    <button
                      className='flex justify-center items-center place-self-center'
                      onClick={() => {
                        toggleScheduleOpen(!scheduleOpen);
                      }}
                      aria-label={
                        scheduleOpen ? 'Close Schedule' : 'Show Schedule'
                      }
                      aria-expanded={scheduleOpen}
                      // as="button"
                    >
                      <p className='break-all mt-1'>
                        {openNow ? (
                          <>
                            <span className='text-action-blue font-bold'>
                              {'Open Now - '}
                            </span>
                            <span>{'Closes at ' + getClosedHour()}</span>
                          </>
                        ) : (
                          <span className='text-action-blue font-bold'>
                            {'Closed Now'}
                          </span>
                        )}
                      </p>
                      <ToggleScheduleContainer className=' place-self-center'>
                        <svg
                          className={cx(
                            'w-6 h-6 text-mattamy-blue transform mt-1',
                            {
                              'rotate-90': scheduleOpen,
                              '-rotate-90': !scheduleOpen,
                            },
                          )}
                          viewBox='0 0 24 24'
                          fill='none'
                          onClick={() => {
                            toggleScheduleOpen(!scheduleOpen);
                          }}
                        >
                          <path
                            fill='currentColor'
                            d='M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z'
                          />
                        </svg>
                      </ToggleScheduleContainer>
                    </button>
                  </CardDescription>
                  <CardDescription>
                    <div className='w-full  z-20'>
                      {scheduleOpen && (
                        <animated.div style={expand}>
                          {renderSchedule()}
                        </animated.div>
                      )}
                    </div>
                  </CardDescription>
                </>
              )}
              <div
                className={cx('p-4 flex flex-col rounded mt-8', {
                  'bg-light-blue ':
                    (firstCTAText && firstCTALink) ||
                    (secondCTAText && secondCTALink),
                })}
              >
                {((firstCTAText && firstCTALink) ||
                  (secondCTAText && secondCTALink)) && (
                  <p className='font-trade-gothic-20 text-xl mb-2 text-mattamy-blue text-center'>
                    Schedule an appointment
                  </p>
                )}
                {firstCTAText && firstCTALink && (
                  <StyledButton
                    color={'action-blue'}
                    variant='filled'
                    className='block text-base mb-2 pt-0 pb-0 md:w-full'
                    onClick={() => {
                      window.dataLayer.push({ event: 'schedule-appointment' });
                      if (trackEventId) {
                        track({ eventId: trackEventId });
                      }
                      window.Calendly.initPopupWidget({
                        url: firstCTALink,
                      });
                    }}
                    buttonDescription={firstCTAText}
                  >
                    {firstCTAText}
                  </StyledButton>
                )}
                {secondCTAText && secondCTALink && (
                  <StyledButton
                    color={'action-blue'}
                    variant='filled'
                    className='block text-base mt-1 pt-0 pb-0 md:w-full'
                    onClick={() => {
                      window.dataLayer.push({
                        event: 'schedule-appointment-virtual',
                      });
                      if (trackEventId) {
                        track({ eventId: trackEventId });
                      }
                      window.Calendly.initPopupWidget({
                        url: secondCTALink,
                      });
                    }}
                    buttonDescription={secondCTAText}
                  >
                    {secondCTAText}
                  </StyledButton>
                )}
              </div>
            </ProductInfo>
          </div>
        </div>
      </Card>
    </CardContainer>
  );
};

SaleCenterCommunityCard.propTypes = {
  props: PropTypes.object,
};

export default SaleCenterCommunityCard;
